/**
 * THE MOTION'S NUMBERS, IN ONE PLACE (2026-11-04; the handoff of 2026-11-03,
 * `docs/design-handoff-2026-11-03/design-tokens.json`). Every number a
 * script drives lives here, typed and frozen; every number the stylesheet
 * drives is a `--pc-*` custom property in `tokens.css`. `motion-tokens.test.ts`
 * holds both against the committed handoff, so the code and Codex's file
 * cannot drift apart without a test saying so. The values are Codex's; this
 * module is the plumbing that carries them.
 */

/** The crossing between the study and the book: the dissolve, its grain and its edges (the brief's "Transition between coach and story"). */
export const THRESHOLD = {
  durationMs: 620,
  /** The grain's and the edges' common strength. */
  strength: 0.68,
  /** The backdrop under the two pages: the coach's desk on one side of the crossing, the story's illustration surround on the other. */
  backdropCoach: "#171d27",
  backdropStory: "#e8e3d9",
  /** Where, in `t`, the outgoing page has faded (0 to 0.48) and the incoming has arrived (0.35 to 1). */
  outgoing: { from: 0, to: 0.48 },
  incoming: { from: 0.35, to: 1 },
  /** The envelope `E` the chromatic offsets and the edges follow. */
  chromatic: { attack: { from: 0.2, to: 0.43 }, release: { from: 0.57, to: 0.8 } },
  /** The envelope `G` the grain and the veil follow. */
  grain: {
    attack: { from: 0.12, to: 0.42 },
    release: { from: 0.58, to: 0.88 },
    seed: 63891,
    paddingPx: 4,
    exponent: 1.4,
    opacityFactor: 0.27,
    maxDpr: 2,
  },
  /** The three channel copies' displacement, in CSS pixels. */
  rgb: {
    separationPx: 0.75,
    wigglePx: 0.35,
    xCycles: 2.4,
    yCycles: 1.8,
    yBase: 0.12,
    yWiggle: 0.55,
    yPhase: Math.PI / 3,
  },
  veil: { peakOpacity: 0.08 },
  divider: { opacityFactor: 0.35, shiftPx: 1.4 },
  headerRule: { opacityFactor: 0.5, cyanStop: 0.38, roseStop: 0.57 },
} as const;

/** The light that travels once around a card the hand has just raised (the brief's first glow). */
export const SELECTION_SWEEP = {
  durationMs: 1150,
  perimeterFraction: 0.055,
  strokePx: 1.15,
  blurPx: 3,
  shadowOpacity: 0.55,
  peakOpacity: 0.55,
  fadeInEnd: 0.1,
  fadeOutStart: 0.82,
} as const;

/** The fray and the shake on the study's buttons (the brief's four rows), as the stylesheet carries them in `--pc-fray-*`. */
export const FRAY = {
  textHover: { ms: 270, amplitudePx: 1.2 },
  textActivate: { ms: 340, amplitudePx: 1.8 },
  frameHover: { ms: 190, amplitudePx: 0.72, edgeMs: 270, edgeAmplitudePx: 1.2 },
  frameActivate: { ms: 270, amplitudePx: 1.2, edgeMs: 270, edgeAmplitudePx: 1.2 },
} as const;
